// ── Flow Step Keys ──
// Values must match the `key` property on each entry in adhdFlowSteps
// so that goToStep(AdhdFlowStep.X) resolves to the correct array position.

export enum AdhdFlowStep {
  WELCOME = 'welcome',
  CARE_PATHWAY = 'carePathway',
  CONSENT = 'consent',
  DETAILS = 'patientInfo',
  FEE_ESTIMATE = 'fee',
  MEDICAL_HISTORY = 'medicalHistory',
  BOOKING = 'booking',
  BOOKING_CONFIRMATION = 'bookingConfirmation',
  COMPLETION = 'completion',
}

export enum AdhdQuestionnaireFlowStep {
  AGE_RANGE = 'ageRange',
  ASSESSMENT_INTRO = 'assessmentIntro',
  ASSESSMENT_QUESTIONNAIRE = 'questionnaireAnswers',
  IDENTITY_CONFIRM = 'identityConfirm',
  COMPLETION = 'completion',
}

export const ADHD_BASE = '/adhd';
export const ADHD_BOOKING_PATH = '/booking';
export const QUESTIONNAIRE_BASE = '/adhd/questionnaire';

export const STEP_BAR_LABELS = [
  'Consent',
  'Details',
  'Fee',
  'Medical',
  'Booking',
];

// ── Browser storage keys ──
// localStorage only; cleared when questionnaire is successfully submitted.

/** Questionnaire answer draft; full key is `${prefix}:${templateId}`. */
export const ADHD_QUESTIONNAIRE_DRAFT_KEY_PREFIX = 'adhd-questionnaire-draft';

/** Young DIVA-5 draft prefix, kept apart from the adult draft. */
export const ADHD_YOUNG_QUESTIONNAIRE_DRAFT_KEY_PREFIX = 'adhd-questionnaire-draft-child';

/** Booking details saved after successful appointment; powers /booked and questionnaire back-nav. */
export const ADHD_BOOKING_CONFIRMATION_KEY = 'adhd-booking-confirmation';

// ── Assessment & Questionnaire ──

export const ASSESSMENT_TYPE = 'ADHD';
export const QUESTIONNAIRE_TEMPLATE_ID = 'adhd-assessment';
export const DEFAULT_QUESTIONNAIRE_VERSION = 1;

/** Young DIVA-5 questionnaire, for patients aged YOUNG_QUESTIONNAIRE_MIN_AGE to _MAX_AGE. */
export const YOUNG_QUESTIONNAIRE_TEMPLATE_ID = 'adhd-assessment-young';

/** Each template is pinned to one published version. */
export const QUESTIONNAIRE_TEMPLATE_VERSIONS: Record<string, number> = {
  [QUESTIONNAIRE_TEMPLATE_ID]: 2,
  [YOUNG_QUESTIONNAIRE_TEMPLATE_ID]: 1,
};

// Young DIVA-5 is for ages 6 to 17 inclusive (product decision, SE-742); 18 and over gets
// the adult questionnaire and under 6 is not supported.
export const YOUNG_QUESTIONNAIRE_MIN_AGE = 6;
export const YOUNG_QUESTIONNAIRE_MAX_AGE = 17;

export const CONSENT_CATEGORY_MAP: Record<string, string> = {
  multiAppt: 'Fees and Cancellation Policy',
  emergency: 'Acknowledgments',
  privacy: 'Acknowledgments',
  cancellation: 'Fees and Cancellation Policy',
};

// ── Animation & Timing ──

export const DUAL_PERIOD_AUTO_ADVANCE_DELAY_MS = 600;
export const RADIO_AUTO_ADVANCE_DELAY_MS = 400;
export const FREQUENCY_AUTO_ADVANCE_DELAY_MS = 350;
export const CARD_TRANSITION_DURATION_MS = 220;

// ── Patient Details Options ──

export const AUSTRALIAN_STATES = [
  'ACT',
  'NSW',
  'NT',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
] as const;

export const GENDER_OPTIONS = [
  'Male',
  'Female',
  'Non-binary',
  'Prefer not to say',
] as const;

export const CULTURAL_BACKGROUND_OPTIONS = [
  'Aboriginal',
  'Torres Strait Islander',
  'Both Aboriginal & Torres Strait Islander',
  'Not Aboriginal or Torres Strait Islander',
  'Prefer not to say',
] as const;

export const REFERRAL_SOURCE_OPTIONS = [
  'Facebook',
  'Google Ad',
  'Google search',
  'Phone enquiry',
  'Online chat',
  'Family/Friend',
  'Other',
] as const;

export const RELATIONSHIPS = [
  { label: 'Mother', value: 'mother' },
  { label: 'Father', value: 'father' },
  { label: 'Grandparent', value: 'grandparent' },
  { label: 'Aunt/Uncle', value: 'aunt/uncle' },
  { label: 'Social Worker', value: 'social worker' },
  { label: 'Carer', value: 'carer' },
  { label: 'Other', value: 'other' },
] as const;

export const MEDICARE_TYPE_OPTIONS = [
  { value: 'medicare', label: 'Medicare Card' },
  { value: 'dva', label: 'DVA Card' },
  { value: 'none', label: 'Neither' },
] as const;

export const DVA_CARD_TYPES = [
  'White',
  'Gold',
  'Orange',
] as const;

// ── Medical History Options ──

export const YES_NO_OPTIONS = ['Yes', 'No'] as const;

export const MARITAL_STATUS_OPTIONS = [
  'Single',
  'De facto',
  'Married',
  'Separated',
  'Divorced',
  'Widowed',
] as const;

export const LIVING_SITUATION_OPTIONS = [
  'Own home',
  'Renting',
  'Social housing',
  'No housing',
  'Other',
] as const;

export const SPECIALIST_TYPE_OPTIONS = [
  'Psychiatrist',
  'Psychologist / Counsellor',
  'Paediatrician',
  'Other',
] as const;

export const CONSULT_REASON_OPTIONS = [
  'Diagnostic Clarity (New Diagnosis)',
  'Diagnostic Clarity (Previously Diagnosed)',
  'Medication Review',
  'Other',
] as const;

export const DIAGNOSIS_DECLINED_OPTIONS = [
  'No (this is my first diagnosis)',
  'No (previous diagnosis — requesting a review)',
  'Yes (I want a second opinion)',
] as const;

export const PREVIOUS_DIAGNOSIS_OPTIONS = [
  'ADHD',
  'Autism',
  'Intellectual disability',
  'Anxiety and/or depression',
  'Bipolar disorder',
  'OCD',
  'PTSD',
  'Alcohol use disorder',
  'Other substance use disorder',
  'Borderline personality disorder',
  'Psychotic disorder',
  'Eating disorder',
  'Sleep disorder',
  'Other',
  'None of the above',
] as const;

export const CURRENT_CONCERNS_OPTIONS = [
  'Anxiety/depression',
  'Significant mood changes',
  'Obsessive or compulsive symptoms',
  'Trauma-related symptoms',
  'Alcohol use',
  'Cannabis or other recreational drug use',
  'Personality-related difficulties',
  'Psychosis/ paranoia',
  'Eating-related concerns',
  'Sleep difficulties',
  'Suicidal attempt or self-harm history',
  'Currently receiving care from a public or community mental health service',
  'Other',
  'None of the above',
] as const;

export const EXCLUSIVE_DIAGNOSIS_OPTIONS = [
  'None of the above',
] as const;

export const SUPPORTING_DOCUMENT_LABELS = [
  {
    label: 'GP Referral Letter (walk-in / self-referred only)',
    description: 'Only needed if your GP has not already sent your referral to Telecare',
  },
  { label: 'Childhood school / teacher reports' },
  { label: 'Previous psychological or psychiatric reports' },
  { label: 'Other relevant medical records' },
];

// ── Calendar & Booking ──

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April',
  'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December',
] as const;

export const WEEKDAY_LABELS = [
  'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat',
] as const;

export const ACCEPTED_FILE_TYPES = 'image/png, image/jpeg, application/pdf, .doc, .docx';

export const UPLOAD_ENDPOINT = '/files';

export interface ConsentItem {
  key: string;
  icon: string;
  title: string;
  summary: string;
  details: string;
  link?: string;
  linkLabel?: string;
  feeInfoBox?: {
    title: string;
    intro: string;
    bullets: string[];
    footer: string;
  };
}

export const consentItems: ConsentItem[] = [
  {
    key: 'multiAppt',
    icon: '📋',
    title: 'Assessment Appointments & Fees',
    summary: 'Our specialists conduct independent assessments \u2014 the outcome may not align with prior diagnoses or personal expectations. In some cases, more than one session may be required. Our team will confirm your exact fee before your appointment \u2014 no surprises.',
    details: 'Telecare specialists conduct independent medical assessments. The outcome may not align with any prior diagnosis or your personal expectations.\n\nIn some cases, your psychiatrist may require more than one session to complete your assessment \u2014 for example, if your presentation is complex or additional clinical information is needed. If this applies to you, our team will let you know in advance, and an additional fee will apply.\n\nYou will have the opportunity to upload supporting documents (e.g. school reports, prior assessment letters, reports from previous health professionals) in the next steps of this portal today. Providing these is recommended and will help your psychiatrist prepare.\n\nFollowing your assessment, our social workers are available for ADHD coaching and psychoeducation sessions. This is not ongoing psychiatric management. Psychiatrist review appointments may be arranged at your GP\u2019s request, and a separate fee applies.\n\nYour fee estimate is available on the Telecare website and will be outlined in the following steps. Our team will confirm your exact fee before your appointment \u2014 we will never proceed with additional billable appointments without your prior agreement. Please note: if you have previously seen a psychiatrist who has billed a psychiatric assessment, you may receive a lower Medicare rebate on subsequent assessments.',
  },
  {
    key: 'emergency',
    icon: '🚨',
    title: 'Emergency & Crisis Care',
    summary: 'Telecare cannot provide emergency assistance. In a crisis, contact your GP, go to your nearest hospital, or call 000.',
    details: 'Telecare and Telecare specialists are unable to provide any emergency assistance.\n\nIn the event of a mental health emergency or crisis, please:\n\u2022 Contact your GP immediately\n\u2022 Go to your nearest hospital emergency department\n\u2022 Contact your local mental health crisis team\n\u2022 Call 000 for emergency services\n\u2022 Call Lifeline on 13 11 14 (24/7 crisis support)\n\nPlease do not rely on Telecare for urgent or emergency mental health needs.',
  },
  {
    key: 'privacy',
    icon: '🔒',
    title: 'Privacy & Data',
    summary: 'Telecare Australia Pty Ltd (ABN 37 632 722 911) collects and stores your personal and health information in accordance with the Australian Privacy Act 1988 and the Australian Privacy Principles. Your information is used solely to provide and coordinate your clinical care.',
    details: 'We collect personal information including your identity, contact, Medicare/DVA, financial, and sensitive health data (such as referral letters, past and current physical and mental health, and medications) to connect you with the appropriate clinical team and provide our services.\n\nYour information will not be shared with third parties without your explicit consent, except where required or authorised by law \u2014 for example, mandatory reporting obligations, court orders, or where necessary to prevent a serious risk to your health or safety or that of another person.\n\nWe may share your information with treating clinicians, IT and data storage providers, and Medicare/Services Australia as reasonably necessary to deliver your care.\n\nAI Technologies: Please note that Telecare may use AI-assisted transcription technology (e.g. Autotranscribe) during your telehealth consultation. Your personal information may be used to train internal AI systems, such as learning from medical language patterns. We implement appropriate security measures and human oversight for all AI-generated outputs.\n\nYour rights:\n\u2022 Access: You may request access to your personal information held by us. An administrative fee may apply.\n\u2022 Correction: You may request correction of inaccurate or incomplete information.\n\u2022 Complaints: If you have a complaint, contact us at adhd@telecare.com.au. If unresolved, you may contact the Office of the Australian Information Commissioner.\n\nFor full details, please read our Privacy Policy at the link below.',
    link: 'https://www.telecare.com.au/privacy-policy',
    linkLabel: 'Read our full Privacy Policy \u2192',
  },
  {
    key: 'cancellation',
    icon: '📅',
    title: 'Cancellation & Missed appointment Policy',
    summary: 'Cancellations within 2 business days or missed appointments incur a $150 fee.',
    details: 'Please understand that our specialists (and your GP where applicable) have reserved their time for you.\n\nIf you must cancel your appointment, we respectfully request two (2) business days notice to offer your appointment to another patient who needs this service.\n\nMissed appointments or appointments cancelled without valid medical reason within two (2) business days of the appointment will incur a fee ($150).\n\nTo cancel or reschedule, please contact us as early as possible via adhd@telecare.com.au or (03) 9034 9950.',
  },
];

export const EVENT_NAMES = {
  REFERRAL_SOURCE_SELECTED: 'referral_source_selected',
  BOOKING_SLOTS_LOADED: 'booking_slots_loaded',
  BOOKING_SLOTS_LOAD_FAILED: 'booking_slots_load_failed',
  BOOKING_CLINICIAN_SELECTED: 'booking_clinician_selected',
  BOOKING_MONTH_CHANGED: 'booking_month_changed',
  BOOKING_DATE_SELECTED: 'booking_date_selected',
  BOOKING_TIME_SEGMENT_SELECTED: 'booking_time_segment_selected',
  BOOKING_SLOT_SELECTED: 'booking_slot_selected',
  BOOKING_SELECTION_LOST: 'booking_selection_lost',
  BOOKING_FEE_DETAILS_OPEN: 'booking_fee_details_open',
  BOOKING_FEE_UNAVAILABLE: 'booking_fee_unavailable',
  BOOKING_FEE_RETRY: 'booking_fee_retry',
  BOOKING_CONFIRM_CLICK: 'booking_confirm_click',
  BOOKING_CONFIRM_SUCCESS: 'booking_confirm_success',
  BOOKING_CONFIRM_ERROR: 'booking_confirm_error',
  BOOKING_BACK_CLICK: 'booking_back_click',
  BOOKING_STEP_EXIT: 'booking_step_exit',
} as const;
